
import { useCallback } from 'react';
import { StudentData } from '../types';
import { enhancedToast } from '@/utils/notifications/enhancedToast';

export const useNotificationsCheck = (students: StudentData[]) => {
  const checkNotifications = useCallback(async () => {
    if (!students || students.length === 0) return [];

    const notifications = [];
    const today = new Date();

    students.forEach(student => {
      if (!student.programs || student.programs.length === 0) return;

      student.programs.forEach(program => {
        // Check for completion notifications
        if (program.completionDate) {
          const completionDate = new Date(program.completionDate);
          const daysUntilCompletion = Math.ceil((completionDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysUntilCompletion <= 7 && daysUntilCompletion >= 0) {
            const notification = {
              id: `completion-${student.id}-${program.title}`,
              type: 'completion',
              message: `${student.name}'s ${program.title} program completes in ${daysUntilCompletion} days`,
              studentId: student.id,
              daysUntil: daysUntilCompletion
            };
            notifications.push(notification);
            
            // Show toast with sound for urgent completions
            if (daysUntilCompletion <= 3) {
              enhancedToast.warning(
                `Program Completion Alert`,
                `${student.name}'s ${program.title} program completes in ${daysUntilCompletion} days`
              );
            }
          }
        }

        // Check for payment notifications
        if (program.nextPaymentDate) {
          const paymentDate = new Date(program.nextPaymentDate);
          const daysUntilPayment = Math.ceil((paymentDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysUntilPayment <= 7 && daysUntilPayment >= 0) {
            const notification = {
              id: `payment-${student.id}-${program.title}`,
              type: 'payment',
              message: `${student.name}'s payment for ${program.title} is due in ${daysUntilPayment} days`,
              studentId: student.id,
              daysUntil: daysUntilPayment
            };
            notifications.push(notification);
            
            // Show toast with sound for urgent payments
            if (daysUntilPayment <= 3) {
              enhancedToast.error(
                `Payment Due Alert`,
                `${student.name}'s payment for ${program.title} is due in ${daysUntilPayment} days`
              );
            }
          }
        }
      });
    });

    return notifications;
  }, [students]);

  const getCompletingStudents = useCallback(async () => {
    if (!students || students.length === 0) return [];

    const completingStudents = [];
    const today = new Date();

    students.forEach(student => {
      if (!student.programs || student.programs.length === 0) return;

      student.programs.forEach(program => {
        if (program.completionDate) {
          const completionDate = new Date(program.completionDate);
          const daysUntilCompletion = Math.ceil((completionDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysUntilCompletion <= 30 && daysUntilCompletion >= 0) {
            completingStudents.push({
              student,
              program,
              daysUntilCompletion
            });
          }
        }
      });
    });

    return completingStudents;
  }, [students]);

  const getUpcomingPayments = useCallback(async (days: number = 7) => {
    if (!students || students.length === 0) return [];

    const upcomingPayments = [];
    const today = new Date();

    students.forEach(student => {
      if (!student.programs || student.programs.length === 0) return;

      student.programs.forEach(program => {
        if (program.nextPaymentDate) {
          const paymentDate = new Date(program.nextPaymentDate);
          const daysUntilPayment = Math.ceil((paymentDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysUntilPayment <= days && daysUntilPayment >= 0) {
            upcomingPayments.push({
              student,
              program,
              daysUntilPayment,
              amount: program.fee || 0
            });
          }
        }
      });
    });

    return upcomingPayments;
  }, [students]);

  return {
    checkNotifications,
    getCompletingStudents,
    getUpcomingPayments
  };
};
